import type { StringKey } from './en'

/**
 * Spanish (es-US): AI-drafted, NOT reviewed. A native US-Spanish reviewer signs
 * these off before Spanish is switched on (DEVELOPER-HANDOFF "Languages and
 * launch countries"; LOCALIZATION "Rules for the AI-drafted strings"): tú,
 * gender-neutral (no vecino/vecina, bienvenido/bienvenida, listo/lista aimed
 * at the person; reword instead), the shortest natural word, English kept
 * where Spanish social apps keep it (feed, story, script).
 *
 * The voice QUESTIONS are not here: they are authored natively per locale,
 * never translated (bank §14), and the Spanish five wait on that reviewer.
 * Picks, phrase lists and endings are voice content and follow the same rule.
 */
export const REVIEWED = false

export const es: Partial<Record<StringKey, string>> = {
  'voice.sheetTitle': 'Tu perfil de voz',
  'voice.cardTitle': 'Completa tu perfil de voz',
  'voice.cardFull': 'Tu perfil de voz está completo',
  'voice.left': 'Faltan {left} de {total}',
  'voice.ofTotal': '{total} de {total}',
  'voice.complete': 'Completo',
  'voice.ringTitle': 'Perfil de voz: faltan {left} de {total}',
  'voice.ringFull': 'Tu perfil de voz está completo',
  'voice.cardLine': 'Cada respuesta ayuda a Emma a escribir guiones que suenan a ti, no a todo el mundo. Como un minuto cada una.',
  'voice.cardFullLine': 'Emma escribe tus guiones como tú hablas. Cada respuesta que diste forma parte de eso.',
  'voice.ecardLine': 'Faltan {left} de {total}. Cada una me ayuda a sonar como tú.',
  'voice.answerOne': 'Responder una',
  'voice.doneForNow': 'Por ahora, ya',
  'voice.loading': 'Un momento.',
  'voice.labelTopic': 'Sobre {topic}',
  'voice.labelTake': 'Haz como que grabas',
  'voice.labelOwn': 'Con tus palabras',
  'voice.typePlaceholder': 'Escríbelo como lo dirías',
  'voice.yourAnswer': 'Tu respuesta',
  'voice.noMic': 'Aquí no hay micrófono, así que escríbelo como lo dirías.',
  'voice.startAnswering': 'Empezar a responder',
  'voice.stop': 'Parar',
  'voice.tapToStart': 'Toca para empezar. Toca otra vez al terminar.',
  'voice.listening': 'Te escucho. Sin prisa.',
  'voice.wrapUp': 'Tómate unos segundos para cerrar.',
  'voice.heardNothing': 'No te escuché. Toca para intentarlo otra vez, o escríbelo.',
  'voice.skipOne': 'Saltar esta',
  'voice.typeInstead': 'Mejor escribir',
  'voice.talkInstead': 'Mejor hablar',
  'voice.next': 'Siguiente',
  'voice.done': 'Listo',
  'voice.skipForNow': 'Saltar por ahora',
  'voice.ack0': 'Anotado.',
  'voice.wholeTitle': 'Esa es toda tu voz.',
  'voice.wholeBody': 'Cada guion de aquí en adelante crece de todo eso. Agrega un tema y crece un poco más.',
  'voice.allForNowTitle': 'Eso es todo por ahora.',
  'voice.allForNowBody': 'Las que saltaste te esperan aquí. Vuelven otro día.',
  'voice.backToIt': 'Seguir',
  'voice.picksLabel': 'Unas elecciones rápidas',
  'voice.picksLede': 'Toca lo que suena a ti. Lo que no toques se queda fuera de tus guiones.',
  'voice.picksEnding': '¿Cuál se parece más a cómo cerrarías un video sobre {topic}?',
  'voice.picksNeither': 'Ninguna. Lo diría a mi manera.',
  'voice.picksAudience': '¿Cómo le dices a la gente que te ve?',
  'voice.picksCaption': '¿Cuál de estos textos podrías haber escrito tú?',
  'voice.picksSwear': 'Malas palabras en tus posts',
  'voice.picksPhrases': 'Estas frases: un toque si las dices mucho, dos si a veces, tres si nunca',
  'voice.offerBrowse': '¿Tienes dos minutos? Unas respuestas más y tus guiones necesitan menos cambios.',
  'voice.offerDay': 'Tu voz ya creció como un {pct}%. {left} respuestas más y dejo de adivinar cómo lo dirías.',
  'voice.offerBuild': 'Buena elección. Responde una pregunta de voz y el próximo guion sonará aún más a ti.',
  'voice.offerAlmost': 'Ya casi. Faltan {left} y tu voz crece por completo.',
  'voice.later': 'Luego',
  'voice.swipeAway': 'Desliza para dejarlo para luego',
  'voice.stopReminding': 'No me lo recuerdes',
  'iv.loading': 'Cargando tus preguntas...',
  'iv.soundOn': 'Con sonido',
  'iv.soundOff': 'Sin sonido',
  'iv.takeEyebrow': 'Haz como que grabas',
  'iv.questionOf': 'Pregunta {n} de {total}',
  'iv.sayAgain': 'Repetir',
  'iv.placeholder': 'Responde como hablas, como un audio para un amigo.',
  'iv.sayInstead': 'Mejor decirlo',
  'iv.stopTalking': 'Dejar de hablar',
  'iv.back': 'Atrás',
  'iv.finishLater': 'Terminar el resto luego',
  'iv.readTitle': 'Esto es lo que escuché.',
  'iv.readLede': 'Voy a convertir tus respuestas en tu perfil de voz. Cada story que armes se escribe con él.',
  'iv.listeningBack': 'Escuchando de nuevo tus {n} respuestas…',
  'iv.needMore': 'Responde al menos {min} para empezar tu perfil de voz, o sigue y respóndelas luego en la app.',
  'iv.makeProfile': 'Crear mi perfil de voz',
  'iv.writing': 'Escribiendo tu perfil de voz',
  'iv.finishing': 'Terminando',
  'iv.takesMinute': 'Esto tarda hasta un minuto.',
  'iv.skipSaved': 'Saltar por ahora. Tus respuestas están guardadas.',
  'iv.youSay': 'Tú dices',
  'iv.youNever': 'Nunca dices',
  'iv.defaultPosition': 'Tu posición por defecto: {pos}. Puedes cambiarla por tema en Temas.',
  'iv.seedling': 'Esta es tu voz recién sembrada. Crece con cada respuesta.',
  'iv.coin': 'Ganaste una moneda: True Voice.',
  'iv.readSpoken': 'Esta es tu voz para empezar. Se acerca más con cada respuesta.',
  'iv.ringStarted': 'Perfil de voz empezado: faltan {left} de {total}',
  'iv.ringBody': 'El resto crece en la app: hay un anillo a mi alrededor y te pregunto de vez en cuando, cuando tengas un minuto. Una sobre cada uno de tus temas, unas cuantas más como estas y unas elecciones rápidas.',
  'iv.ringSkipped': 'Lo que saltaste espera al final de la fila.',
  'settings.voiceProfile': 'Tu perfil de voz',
  'settings.anglePerTopic': 'Tu enfoque por tema',
  'settings.changeInTopics': 'Cambiar en Temas',
  'settings.redoStarter': 'Responder otra vez las primeras cinco',
  'settings.redoStarterLine': 'Las cinco preguntas del inicio, con tus respuestas listas para editar. El resto vive en el anillo de Emma.',
  'settings.voiceSaved': 'Tu perfil de voz está guardado.',
}
